import { useState, useEffect } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'https://hello-reviews-api.b-9f2.workers.dev';

interface CourseProgress {
  [key: string]: boolean; // itemId -> completed
}

interface LearningLogEntry {
  id?: number;
  date: string;
  what_learned: string;
  how_to_use?: string;
  week_number?: number;
}

interface WeekData {
  number: number;
  title: string;
  subtitle: string;
  readings: { id: string; title: string; desc: string; url?: string }[];
  exercises: { id: string; title: string; desc: string }[];
  milestones: { id: string; title: string }[];
}

interface AIFeedback {
  id?: number;
  question: string;
  ai_response: string;
  helpful?: boolean;
  created_at?: string;
}

export default function CourseOfStudy() {
  const [progress, setProgress] = useState<CourseProgress>({});
  const [currentWeek, setCurrentWeek] = useState(1);
  const [showLearningLog, setShowLearningLog] = useState(false);
  const [learningLog, setLearningLog] = useState<LearningLogEntry[]>([]);
  const [newLogEntry, setNewLogEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    what_learned: '',
    how_to_use: ''
  });
  const [showAIHelp, setShowAIHelp] = useState(false);
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiHistory, setAiHistory] = useState<AIFeedback[]>([]);
  const [loadingAI, setLoadingAI] = useState(false);

  // Course data structure
  const weeks: WeekData[] = [
    {
      number: 1,
      title: "Review Psychology & Consumer Behavior",
      subtitle: "Understanding why reviews work and what motivates people",
      readings: [
        { id: "week1-reading-1", title: "The Social Proof Effect", desc: "Robert Cialdini's work on influence and persuasion", url: "https://www.influenceatwork.com/principles-of-persuasion/" },
        { id: "week1-reading-2", title: "BrightLocal Consumer Review Survey", desc: "Latest 3 years to see trends", url: "https://www.brightlocal.com/research/local-consumer-review-survey/" },
        { id: "week1-reading-3", title: "Google My Business Best Practices", desc: "What's allowed/not allowed, algorithm for review ranking", url: "https://support.google.com/business/answer/2911163" },
        { id: "week1-reading-4", title: "Yelp's Review System", desc: "How Yelp filters reviews, Consumer Trust Report", url: "https://www.yelp-support.com/article/Why-is-a-review-not-currently-recommended" },
      ],
      exercises: [
        { id: "week1-exercise-1", title: "Review Analysis Exercise", desc: "Analyze 10 local businesses - reviews count, rating, recency, response rate" },
        { id: "week1-exercise-2", title: "Consumer Interviews", desc: "Ask 5 people about their review-leaving behavior" },
        { id: "week1-exercise-3", title: "Personal Review Audit", desc: "Analyze your own review-leaving patterns" },
      ],
      milestones: [
        { id: "week1-milestone-1", title: "Can explain why reviews matter in 30 seconds" },
        { id: "week1-milestone-2", title: "Know key stats from memory (87%, 52%, etc.)" },
        { id: "week1-milestone-3", title: "Understand what motivates someone to leave a review" },
      ]
    },
    {
      number: 2,
      title: "Technical Deep Dive",
      subtitle: "APIs, platforms, automation, and how everything works",
      readings: [
        { id: "week2-reading-1", title: "Google My Business API Documentation", desc: "Review endpoints, location management, rate limits", url: "https://developers.google.com/my-business/content/overview" },
        { id: "week2-reading-2", title: "Yelp Fusion API", desc: "What data can you pull? What can't you do?", url: "https://docs.developer.yelp.com/docs/fusion-intro" },
        { id: "week2-reading-3", title: "Facebook Reviews Graph API", desc: "Permissions and capabilities", url: "https://developers.facebook.com/docs/graph-api/reference/page/ratings/" },
        { id: "week2-reading-4", title: "Review Schema Markup", desc: "How reviews show up in search results", url: "https://schema.org/Review" },
      ],
      exercises: [
        { id: "week2-exercise-1", title: "API Exploration", desc: "Free trial of Podium/BrightLocal - screenshot workflows" },
        { id: "week2-exercise-2", title: "Widget Teardown", desc: "Inspect 5 review widgets - how they work, page load impact" },
        { id: "week2-exercise-3", title: "Automation Flow Mapping", desc: "Map customer journey from visit to review" },
        { id: "week2-exercise-4", title: "Platform Comparison Matrix", desc: "Compare Google/Yelp/Facebook/industry platforms" },
      ],
      milestones: [
        { id: "week2-milestone-1", title: "Know which platforms have APIs and which don't" },
        { id: "week2-milestone-2", title: "Understand technical constraints of automation" },
        { id: "week2-milestone-3", title: "Can explain why some reviews show up and others don't" },
      ]
    },
    {
      number: 3,
      title: "Competitive Analysis & Business Models",
      subtitle: "Understanding the market, competitors, and positioning",
      readings: [
        { id: "week3-reading-1", title: "Podium Deep Dive", desc: "Pricing, target market, features, sales approach", url: "https://www.podium.com/" },
        { id: "week3-reading-2", title: "BrightLocal Analysis", desc: "Agency focus, white-label, pricing tiers", url: "https://www.brightlocal.com/local-seo-tools/reputation-manager/" },
        { id: "week3-reading-3", title: "Birdeye Study", desc: "Enterprise positioning, case studies, integrations", url: "https://birdeye.com/" },
        { id: "week3-reading-4", title: "Senja Comparison", desc: "DIY testimonials vs. our public reviews", url: "https://senja.com/" },
        { id: "week3-reading-5", title: "G2 Reviews Analysis", desc: "Read 50+ reviews of each competitor", url: "https://www.g2.com/categories/reputation-management" },
      ],
      exercises: [
        { id: "week3-exercise-1", title: "Competitor Feature Matrix", desc: "Spreadsheet with all competitors and features" },
        { id: "week3-exercise-2", title: "Pricing Analysis", desc: "Document all pricing tiers, calculate value leaps" },
        { id: "week3-exercise-3", title: "Sales Call Research", desc: "Book 2-3 competitor demos, take detailed notes" },
        { id: "week3-exercise-4", title: "Customer Journey Mapping", desc: "Map how customers find and onboard with competitors" },
      ],
      milestones: [
        { id: "week3-milestone-1", title: "Can name 5 competitors and differentiate from each" },
        { id: "week3-milestone-2", title: "Know what customers hate about existing solutions" },
        { id: "week3-milestone-3", title: "Understand pricing landscape and can justify our price" },
      ]
    },
    {
      number: 4,
      title: "Sales, Objections & Execution",
      subtitle: "Selling confidently and delivering results",
      readings: [
        { id: "week4-reading-1", title: "Fanatical Prospecting (Audiobook)", desc: "Focus on objection handling chapters", url: "https://www.audible.com/pd/Fanatical-Prospecting-Audiobook/B01B07FM5Y" },
        { id: "week4-reading-2", title: "Moz Local SEO Guide", desc: "How reviews impact local pack rankings", url: "https://moz.com/learn/seo/local-seo" },
        { id: "week4-reading-3", title: "SaaS Customer Onboarding", desc: "Best practices for first 90 days", url: "https://www.userguiding.com/blog/saas-onboarding-best-practices/" },
        { id: "week4-reading-4", title: "Review Request Templates", desc: "Case studies of businesses getting 100 reviews in 90 days", url: "https://www.brightlocal.com/blog/review-request-templates/" },
      ],
      exercises: [
        { id: "week4-exercise-1", title: "Objection Handling Script", desc: "Write responses to 20 common objections" },
        { id: "week4-exercise-2", title: "ICP Deep Dive", desc: "Pick ONE vertical, research deeply" },
        { id: "week4-exercise-3", title: "Demo Flow Creation", desc: "Build 15-minute demo, record yourself" },
        { id: "week4-exercise-4", title: "Delivery Playbook", desc: "Document Month 1: week by week plan" },
        { id: "week4-exercise-5", title: "ROI Calculator", desc: "Build spreadsheet showing break-even point" },
      ],
      milestones: [
        { id: "week4-milestone-1", title: "Can handle top 10 objections smoothly" },
        { id: "week4-milestone-2", title: "Know ideal customer inside and out" },
        { id: "week4-milestone-3", title: "Can deliver compelling 15-minute demo" },
        { id: "week4-milestone-4", title: "Have clear delivery process for Month 1" },
      ]
    }
  ];

  // Load progress from API
  useEffect(() => {
    fetchProgress();
    fetchLearningLog();
  }, []);

  const fetchProgress = async () => {
    try {
      const response = await fetch(`${API_URL}/api/course/progress`);
      const data = await response.json();
      if (data.success && data.data) {
        const progressMap: CourseProgress = {};
        data.data.forEach((item: any) => {
          progressMap[item.item_id] = item.completed === 1;
        });
        setProgress(progressMap);
      }
    } catch (err) {
      console.error('Failed to fetch progress:', err);
    }
  };

  const fetchLearningLog = async () => {
    try {
      const response = await fetch(`${API_URL}/api/course/log`);
      const data = await response.json();
      if (data.success && data.data) {
        setLearningLog(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch learning log:', err);
    }
  };

  const toggleItem = async (itemId: string) => {
    const newState = !progress[itemId];
    setProgress({ ...progress, [itemId]: newState });

    try {
      await fetch(`${API_URL}/api/course/progress/${itemId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed: newState })
      });
    } catch (err) {
      console.error('Failed to update progress:', err);
      // Revert on error
      setProgress({ ...progress, [itemId]: !newState });
    }
  };

  const addLearningLogEntry = async () => {
    if (!newLogEntry.what_learned.trim()) return;

    try {
      const response = await fetch(`${API_URL}/api/course/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newLogEntry,
          week_number: currentWeek
        })
      });

      const data = await response.json();
      if (data.success) {
        setLearningLog([data.data, ...learningLog]);
        setNewLogEntry({
          date: new Date().toISOString().split('T')[0],
          what_learned: '',
          how_to_use: ''
        });
      }
    } catch (err) {
      console.error('Failed to add learning log:', err);
    }
  };

  const fetchAIHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/course/ai/history?week=${currentWeek}`);
      const data = await response.json();
      if (data.success && data.data) {
        setAiHistory(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch AI history:', err);
    }
  };

  const askAI = async () => {
    if (!aiQuestion.trim()) return;

    setLoadingAI(true);
    try {
      const response = await fetch(`${API_URL}/api/course/ai/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: aiQuestion,
          week_number: currentWeek,
          context: weekData.title
        })
      });

      const data = await response.json();
      if (data.success && data.data) {
        setAiHistory([data.data, ...aiHistory]);
        setAiQuestion('');
      }
    } catch (err) {
      console.error('Failed to ask AI:', err);
      alert('Failed to get AI response. Please try again.');
    } finally {
      setLoadingAI(false);
    }
  };

  // Load AI history when week changes
  useEffect(() => {
    if (showAIHelp) {
      fetchAIHistory();
    }
  }, [currentWeek, showAIHelp]);

  // Calculate stats
  const weekData = weeks[currentWeek - 1];
  const allItems = [
    ...weekData.readings.map(r => r.id),
    ...weekData.exercises.map(e => e.id),
    ...weekData.milestones.map(m => m.id)
  ];
  const completedItems = allItems.filter(id => progress[id]).length;
  const totalItems = allItems.length;
  const weekProgress = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  // Overall progress
  const allItemsAllWeeks = weeks.flatMap(w => [
    ...w.readings.map(r => r.id),
    ...w.exercises.map(e => e.id),
    ...w.milestones.map(m => m.id)
  ]);
  const totalCompleted = allItemsAllWeeks.filter(id => progress[id]).length;
  const overallProgress = Math.round((totalCompleted / allItemsAllWeeks.length) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-green-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">📚 Course of Study</h1>
          <p className="text-xl text-gray-600 mb-4">30 Days to Reviews Business Mastery</p>

          {/* Overall Progress Bar */}
          <div className="max-w-md mx-auto">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Overall Progress</span>
              <span className="font-bold">{overallProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-purple-600 to-blue-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {totalCompleted} of {allItemsAllWeeks.length} items completed
            </p>
          </div>
        </div>

        {/* Week Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {weeks.map((week) => {
            const weekItems = [
              ...week.readings.map(r => r.id),
              ...week.exercises.map(e => e.id),
              ...week.milestones.map(m => m.id)
            ];
            const completed = weekItems.filter(id => progress[id]).length;
            const total = weekItems.length;
            const isComplete = completed === total;

            return (
              <button
                key={week.number}
                onClick={() => setCurrentWeek(week.number)}
                className={`px-6 py-3 rounded-lg font-medium whitespace-nowrap transition-all ${
                  currentWeek === week.number
                    ? 'bg-blue-600 text-white shadow-lg scale-105'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Week {week.number} {isComplete && '✅'}
                <div className="text-xs mt-1 opacity-75">
                  {completed}/{total}
                </div>
              </button>
            );
          })}
        </div>

        {/* Current Week Content */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <div className="mb-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">{weekData.title}</h2>
            <p className="text-lg text-gray-600">{weekData.subtitle}</p>

            {/* Week Progress */}
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Week Progress</span>
                <span className="font-bold">{weekProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${weekProgress}%` }}
                />
              </div>
            </div>
          </div>

          {/* Readings */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              📖 Required Reading
              <span className="text-sm font-normal text-gray-600">
                ({weekData.readings.filter(r => progress[r.id]).length}/{weekData.readings.length})
              </span>
            </h3>
            <div className="space-y-3">
              {weekData.readings.map((reading) => (
                <label
                  key={reading.id}
                  className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    progress[reading.id]
                      ? 'bg-green-50 border-green-300'
                      : 'bg-gray-50 border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={progress[reading.id] || false}
                    onChange={() => toggleItem(reading.id)}
                    className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-semibold text-gray-900">{reading.title}</div>
                      {reading.url && (
                        <a
                          href={reading.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                        >
                          🔗 Read
                        </a>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">{reading.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Exercises */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              💪 Practical Exercises
              <span className="text-sm font-normal text-gray-600">
                ({weekData.exercises.filter(e => progress[e.id]).length}/{weekData.exercises.length})
              </span>
            </h3>
            <div className="space-y-3">
              {weekData.exercises.map((exercise) => (
                <label
                  key={exercise.id}
                  className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    progress[exercise.id]
                      ? 'bg-blue-50 border-blue-300'
                      : 'bg-gray-50 border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={progress[exercise.id] || false}
                    onChange={() => toggleItem(exercise.id)}
                    className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">{exercise.title}</div>
                    <div className="text-sm text-gray-600">{exercise.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Milestones */}
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              🎯 Key Takeaways
              <span className="text-sm font-normal text-gray-600">
                ({weekData.milestones.filter(m => progress[m.id]).length}/{weekData.milestones.length})
              </span>
            </h3>
            <div className="space-y-3">
              {weekData.milestones.map((milestone) => (
                <label
                  key={milestone.id}
                  className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    progress[milestone.id]
                      ? 'bg-purple-50 border-purple-300'
                      : 'bg-gray-50 border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={progress[milestone.id] || false}
                    onChange={() => toggleItem(milestone.id)}
                    className="mt-1 h-5 w-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">{milestone.title}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Learning Log Section */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl font-bold text-gray-900">📝 Daily Learning Log</h3>
            <button
              onClick={() => setShowLearningLog(!showLearningLog)}
              className="px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium hover:bg-gray-200"
            >
              {showLearningLog ? 'Hide Log' : `Show Log (${learningLog.length})`}
            </button>
          </div>

          <p className="text-gray-600 mb-4">
            Track what you learn each day and how you'll use it. This becomes your competitive advantage.
          </p>

          {/* Add Entry Form */}
          <div className="bg-blue-50 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <input
                type="date"
                value={newLogEntry.date}
                onChange={(e) => setNewLogEntry({ ...newLogEntry, date: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              />
              <input
                type="text"
                value={newLogEntry.what_learned}
                onChange={(e) => setNewLogEntry({ ...newLogEntry, what_learned: e.target.value })}
                placeholder="What I learned..."
                className="px-3 py-2 border border-gray-300 rounded-lg md:col-span-2"
              />
            </div>
            <div className="flex gap-3">
              <input
                type="text"
                value={newLogEntry.how_to_use}
                onChange={(e) => setNewLogEntry({ ...newLogEntry, how_to_use: e.target.value })}
                placeholder="How I'll use it..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
              />
              <button
                onClick={addLearningLogEntry}
                disabled={!newLogEntry.what_learned.trim()}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Entry
              </button>
            </div>
          </div>

          {/* Learning Log Entries */}
          {showLearningLog && (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {learningLog.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No entries yet. Start logging your learning!</p>
              ) : (
                learningLog.map((entry) => (
                  <div key={entry.id} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex justify-between items-start mb-1">
                      <span className="text-sm font-medium text-gray-500">
                        {new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                      {entry.week_number && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          Week {entry.week_number}
                        </span>
                      )}
                    </div>
                    <div className="font-semibold text-gray-900 mb-1">{entry.what_learned}</div>
                    {entry.how_to_use && (
                      <div className="text-sm text-gray-600 italic">→ {entry.how_to_use}</div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* AI Help Section */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl shadow-lg p-8 mb-6 border-2 border-purple-200">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl font-bold text-gray-900">🤖 Ask AI Coach</h3>
            <button
              onClick={() => setShowAIHelp(!showAIHelp)}
              className="px-4 py-2 bg-purple-100 rounded-lg text-sm font-medium hover:bg-purple-200 text-purple-900"
            >
              {showAIHelp ? 'Hide AI' : `Ask Question (${aiHistory.length} past)`}
            </button>
          </div>

          <p className="text-gray-700 mb-4">
            Stuck? Confused? Need practical advice? Ask your AI coach powered by Cloudflare AI.
          </p>

          {/* Ask AI Form */}
          <div className="bg-white rounded-lg p-4 mb-4 border-2 border-purple-200">
            <div className="flex gap-3">
              <input
                type="text"
                value={aiQuestion}
                onChange={(e) => setAiQuestion(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && askAI()}
                placeholder="e.g., How do I handle the objection 'reviews don't matter'?"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                disabled={loadingAI}
              />
              <button
                onClick={askAI}
                disabled={!aiQuestion.trim() || loadingAI}
                className="px-8 py-3 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loadingAI ? (
                  <>
                    <span className="inline-block animate-spin">⏳</span>
                    Thinking...
                  </>
                ) : (
                  <>Ask AI</>
                )}
              </button>
            </div>
          </div>

          {/* AI History */}
          {showAIHelp && (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {aiHistory.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No questions asked yet. Try asking something!</p>
              ) : (
                aiHistory.map((feedback) => (
                  <div key={feedback.id} className="bg-white border-2 border-purple-100 rounded-lg p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="text-2xl">💬</div>
                      <div className="flex-1">
                        <div className="font-bold text-gray-900 mb-1">{feedback.question}</div>
                        {feedback.created_at && (
                          <div className="text-xs text-gray-500">
                            {new Date(feedback.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit'
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="text-2xl">🤖</div>
                      <div className="flex-1">
                        <div className="text-gray-700 whitespace-pre-wrap">{feedback.ai_response}</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <a
            href="/#focus"
            className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all text-center"
          >
            <div className="text-3xl mb-2">🎯</div>
            <div className="font-bold text-lg mb-1">Builder Focus</div>
            <div className="text-sm opacity-90">Check conversation streak</div>
          </a>

          <a
            href="/#log"
            className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all text-center"
          >
            <div className="text-3xl mb-2">📝</div>
            <div className="font-bold text-lg mb-1">Quick Log</div>
            <div className="text-sm opacity-90">Log customer conversations</div>
          </a>

          <a
            href="/COURSE-OF-STUDY.md"
            target="_blank"
            className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all text-center"
          >
            <div className="text-3xl mb-2">📚</div>
            <div className="font-bold text-lg mb-1">Full Curriculum</div>
            <div className="text-sm opacity-90">Read complete document</div>
          </a>
        </div>

        {/* Back Link */}
        <div className="text-center">
          <a href="#home" className="text-blue-600 hover:underline font-medium">
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
}
